import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CoachIdentityEvaluationList from './CoachIdentityEvaluationList';
import ScopedRemoveDialog from './ScopedRemoveDialog';
import { saveCoachIdentityDecision, removeCoachIdentityDecision } from '../services/coachIdentityApi';

// Human text for the non-blocking warnings the server derives. 🔴 NONE of these prevents a save:
// this is a human audit tool, and an auditor who knows a coach has left is better placed than a
// CHECK constraint to decide whether that coach is still the right historical answer.
const WARNING_TEXT = {
  employee_missing_from_dim:
    'The accepted coach is no longer in Dash. The attribution still applies and history is unchanged — the label falls back to the name captured when this was accepted.',
  employee_inactive: 'This coach is currently inactive in Dash. That does not make them the wrong historical answer.',
  employee_franchise_differs: 'This coach now belongs to a different franchise in Dash than when the decision was accepted.',
  employee_not_lk_si_eligible: 'This coach currently carries no LK/SI class in Dash.',
  stale_decision: 'This decision no longer matches any identity in the current review population. It is retained, not withdrawn.',
  no_candidate_on_roster: 'No coach on this franchise roster matches the typed name.',
  multiple_candidates: 'More than one roster coach matches the typed name — choose using your own judgment.',
};

// The three deterministic ways a roster coach can match a typed Form name. Never fuzzy: the only
// operation anywhere is LOWER(LTRIM(RTRIM())), and the same three comparisons decide corroboration
// in the warehouse, so a candidate offered here is a candidate the warehouse would recognise.
const MATCH_LABEL = {
  full_name: 'full name',
  display_name: 'display name',
  first_name: 'first name',
};

function candidateLabel(c) {
  const name = c.coach_display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.employee_id;
  const match = MATCH_LABEL[c.match_type] || c.match_type;
  const eligible = c.is_active === 1 && c.has_lk_si_class === 1;
  const facility = c.home_facility_name ? ` · ${c.home_facility_name}` : '';
  return `${name} — ${match}${facility}${eligible ? '' : ' · not currently eligible'}`;
}

// The decision controls for ONE provisional coach identity.
//
// 🔴 The grain is the identity, not the evaluation. Accepting here settles every evaluation already
// under this identity AND every future evaluation that arrives under it — which is the whole reason
// the legacy queue is workable by a person at all.
export default function CoachIdentityDecisionPanel({ row, candidates, onRowUpdate, onIdentityComplete, onEvaluationSaved }) {
  const [decision, setDecision] = useState(row.decision || '');
  const [employeeId, setEmployeeId] = useState(row.canonical_employee_id || '');
  const [note, setNote] = useState(row.reviewer_note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Re-seed when the server sends a newer version of this row (a save, or a refetch).
  useEffect(() => {
    setDecision(row.decision || '');
    setEmployeeId(row.canonical_employee_id || '');
    setNote(row.reviewer_note || '');
    setError('');
  }, [row.decision, row.canonical_employee_id, row.reviewer_note]);

  // A single eligible candidate is PRESELECTED, never auto-accepted. The auditor still presses
  // Add/Update, because a class-roster name match is evidence about a person, not proof about a
  // decision — measured 2026-09-22, a single rostered coach disagrees with the typed name often
  // enough that silent acceptance would be wrong at scale.
  useEffect(() => {
    if (row.decision) return;                    // already decided; leave the auditor's value alone
    if (decision !== 'link' || employeeId) return;
    const eligible = candidates.filter((c) => c.is_active === 1 && c.has_lk_si_class === 1);
    if (eligible.length === 1) setEmployeeId(eligible[0].employee_id);
  }, [decision, employeeId, candidates, row.decision]);

  const canSave = decision === 'keep_provisional' || (decision === 'link' && employeeId !== '');

  const handleSave = async () => {
    if (!canSave) {
      setError(decision === 'link' ? 'Select the Dash coach to link to.' : 'Choose a decision first.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await saveCoachIdentityDecision({
        com_code: row.com_code,
        typed_name_norm: row.typed_name_norm,
        decision,
        canonical_employee_id: decision === 'link' ? employeeId : undefined,
        reviewer_note: note || undefined,
      });
      if (updated) onRowUpdate(updated);
    } catch (err) {
      setError(err.message || 'Could not save this decision.');
    } finally {
      setSaving(false);
    }
  };

  // Runs only from the confirmation dialog, never from the section button directly.
  const handleRemove = async () => {
    setSaving(true);
    setError('');
    try {
      const updated = await removeCoachIdentityDecision({
        com_code: row.com_code,
        typed_name_norm: row.typed_name_norm,
      });
      if (updated) onRowUpdate(updated);
    } catch (err) {
      setError(err.message || 'Could not remove the identity-wide rule.');
    } finally {
      setSaving(false);
      setConfirmRemove(false);
    }
  };

  // The saved rule in the auditor's words, for the confirmation. Read from the ROW (what is stored),
  // never from the radio buttons (what may have been edited since).
  const savedRuleText = row.decision === 'keep_provisional'
    ? 'Keep as a Form coach'
    : `Link to ${row.employee_display_name || row.snapshot_coach_display_name || row.canonical_employee_id || 'a Dash coach'}`;
  const evalCount = row.evaluation_count || 0;

  const candidateItems = candidates.map((c) => (
    <MenuItem key={c.employee_id} value={c.employee_id}>{candidateLabel(c)}</MenuItem>
  ));

  const warningItems = (row.warnings || []).map((w) => (
    <Alert key={w} severity="info" variant="outlined" sx={{ mb: 1 }}>
      {WARNING_TEXT[w] || w}
    </Alert>
  ));

  return (
    <Box sx={{ maxWidth: 780 }}>
      <Typography variant="subtitle2" component="h3">Identity-wide rule (all evaluations)</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        A rule here applies to every current and future evaluation under this franchise and Form name.
      </Typography>
      {/* Raw Form evidence, never editable. The typed name is the auditor's primary evidence and
          the thing the whole decision is about, so it is shown verbatim rather than normalised. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Raw Form name <strong>“{row.form_raw_name}”</strong> · {row.evaluation_count} evaluation
        {row.evaluation_count === 1 ? '' : 's'}
        {row.first_evaluation ? ` · ${String(row.first_evaluation).slice(0, 10)} → ${String(row.last_evaluation).slice(0, 10)}` : ''}
        {' · '}key <code>{row.reporting_coach_key}</code>
      </Typography>

      {warningItems}

      <FormControl sx={{ mb: 1.5 }}>
        <FormLabel id={`decision-${row.reporting_coach_key}`}>Decision</FormLabel>
        <RadioGroup
          row
          aria-labelledby={`decision-${row.reporting_coach_key}`}
          value={decision}
          onChange={(e) => { setDecision(e.target.value); setError(''); }}
        >
          <FormControlLabel value="link" control={<Radio size="small" />} label="Link to a Dash coach" />
          <FormControlLabel value="keep_provisional" control={<Radio size="small" />} label="Keep as a Form coach" />
        </RadioGroup>
      </FormControl>

      {decision === 'link' && (
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel id={`coach-${row.reporting_coach_key}`}>Dash coach</InputLabel>
          <Select
            labelId={`coach-${row.reporting_coach_key}`}
            label="Dash coach"
            value={employeeId}
            onChange={(e) => { setEmployeeId(e.target.value); setError(''); }}
          >
            {candidateItems}
          </Select>
          {/* 🔴 Franchise-scoped by construction: the server derives the franchise from the identity
              and only ever returns that franchise's coaches, so a conforming UI cannot offer a
              cross-franchise link. The hard server check behind it therefore never fires in normal
              use while remaining the guarantee for anything that bypasses this screen. */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {candidates.length === 0
              ? 'No roster coach in this franchise matches the typed name.'
              : `${candidates.length} candidate${candidates.length === 1 ? '' : 's'} in ${row.com_code}.`}
          </Typography>
        </FormControl>
      )}

      <TextField
        fullWidth
        size="small"
        label="Reviewer note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        sx={{ mb: 1.5 }}
      />

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button variant="contained" size="small" disabled={saving} onClick={handleSave}>
          Add/Update
        </Button>
        {row.decision && (
          // A removal deactivates the row; it never deletes one, and it never touches an
          // evaluation. The identity falls back to the deterministic computed path on the next build.
          <Button variant="outlined" size="small" color="secondary" disabled={saving} onClick={() => setConfirmRemove(true)}>
            Remove identity-wide rule…
          </Button>
        )}
        {row.pending_rebuild === true && (
          <Typography variant="caption" color="text.disabled">
            Saved. CP-06 and CP-08 reflect this on the next warehouse build.
          </Typography>
        )}
      </Box>

      <ScopedRemoveDialog
        open={confirmRemove}
        title="Remove the identity-wide rule?"
        confirmLabel="Remove identity-wide rule"
        busy={saving}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={handleRemove}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          This removes the rule <strong>{savedRuleText}</strong> for{' '}
          <strong>{row.com_code} / “{row.form_raw_name}”</strong>, which applies to all{' '}
          <strong>{evalCount}</strong> current evaluation{evalCount === 1 ? '' : 's'} and any future ones.
        </Typography>
        <Typography variant="body2">
          It does <strong>not</strong> remove any evaluation reviews.
        </Typography>
      </ScopedRemoveDialog>

      {/* 🔴 A DIFFERENT GRAIN BELOW THIS LINE. Everything above settles WHO this identity is, across
          every evaluation it will ever receive. Everything below settles WHICH DASH SESSION one
          single evaluation describes. They are separate tables, separate endpoints and separate
          buttons; the divider is here so neither save can be mistaken for the other. */}
      <Divider sx={{ my: 2.5 }} />
      {/* Progress flows UP to this identity's grid row, so a class save shows in Class audit at
          once. Only the counts are merged -- never a decision field. */}
      <CoachIdentityEvaluationList
        row={row}
        onProgressChange={(counts) => onRowUpdate({ reporting_coach_key: row.reporting_coach_key, ...counts })}
        onIdentityComplete={onIdentityComplete}
        onEvaluationSaved={onEvaluationSaved}
      />
    </Box>
  );
}
